import ExcelJS from 'exceljs';

export const TEMPLATE_TYPES = {
  PLAIN: 'plain',
  EMIRATES: 'emirates',
  EMIRATES_LOCATIONS: 'emirates-locations'
};

const HEADERS = [
  'VoucherNo',
  'ClientBillNo',
  'PayType',
  'Customer',
  'MobileNo',
  'MobileNo2',
  'Emirate',
  'Location',
  'Address',
  'Quantity',
  'ProductPrice',
  'DeliveryInstructions'
];

const COLUMN_WIDTHS = [12, 14, 10, 18, 14, 14, 16, 18, 28, 10, 12, 24];
const DATA_ROW_COUNT = 200;
const EMIRATE_COL = 7; // G
const LOCATION_COL = 8; // H
const PAYTYPE_COL = 3; // C

const toNamedRangeKey = (name) =>
  String(name || '')
    .trim()
    .replace(/ /g, '_');

const downloadBuffer = (buffer, fileName) => {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const styleHeaderRow = (sheet) => {
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1F4E79' }
  };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  headerRow.height = 22;
};

const addPayTypeValidation = (sheet) => {
  for (let row = 2; row <= DATA_ROW_COUNT + 1; row += 1) {
    sheet.getCell(row, PAYTYPE_COL).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: ['"COD,CAD"'],
      showErrorMessage: true,
      errorTitle: 'Invalid Pay Type',
      error: 'Please select COD or CAD'
    };
  }
};

const addListValidation = (sheet, col, formula, title, error) => {
  for (let row = 2; row <= DATA_ROW_COUNT + 1; row += 1) {
    sheet.getCell(row, col).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [formula],
      showErrorMessage: true,
      errorTitle: title,
      error
    };
  }
};

/**
 * Generate and download a bulk order Excel template.
 * @param {'plain'|'emirates'|'emirates-locations'} type
 * @param {{ emirates?: Array<{Id:any, Name:string}>, locationsByEmirate?: Record<string, Array<{Id:any, Name:string}>> }} options
 */
export const downloadBulkOrderTemplate = async (type, options = {}) => {
  const { emirates = [], locationsByEmirate = {} } = options;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Ainsoft Client Portal';
  workbook.created = new Date();

  const ordersSheet = workbook.addWorksheet('Orders');
  ordersSheet.addRow(HEADERS);
  styleHeaderRow(ordersSheet);
  COLUMN_WIDTHS.forEach((width, index) => {
    ordersSheet.getColumn(index + 1).width = width;
  });
  addPayTypeValidation(ordersSheet);

  const fileNames = {
    [TEMPLATE_TYPES.PLAIN]: 'BulkOrder.xlsx',
    [TEMPLATE_TYPES.EMIRATES]: 'BulkOrder_WithEmirates.xlsx',
    [TEMPLATE_TYPES.EMIRATES_LOCATIONS]: 'BulkOrder_WithEmiratesAndLocations.xlsx'
  };

  if (type === TEMPLATE_TYPES.PLAIN) {
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBuffer(buffer, fileNames[type]);
    return;
  }

  const listsSheet = workbook.addWorksheet('Lists');
  listsSheet.state = 'veryHidden';

  const emirateNames = emirates
    .map((e) => (e?.Name || '').trim())
    .filter(Boolean);

  // Column A: Emirates list
  listsSheet.getCell(1, 1).value = 'Emirates';
  emirateNames.forEach((name, index) => {
    listsSheet.getCell(index + 2, 1).value = name;
  });

  const emiratesRangeEnd = Math.max(emirateNames.length + 1, 2);
  const emiratesFormula = `Lists!$A$2:$A$${emiratesRangeEnd}`;
  addListValidation(
    ordersSheet,
    EMIRATE_COL,
    emiratesFormula,
    'Invalid Emirate',
    'Please select an Emirate from the list'
  );

  if (type === TEMPLATE_TYPES.EMIRATES_LOCATIONS) {
    // Each emirate gets its own column of locations + a named range for cascading dropdown
    // ExcelJS definedNames.add(location, name) — location first, then name
    emirateNames.forEach((emirateName, emirateIndex) => {
      const col = emirateIndex + 2; // start at B
      const rangeKey = toNamedRangeKey(emirateName);
      const locations = (locationsByEmirate[emirateName] || [])
        .map((loc) => (loc?.Name || '').trim())
        .filter(Boolean);

      listsSheet.getCell(1, col).value = emirateName;

      if (locations.length === 0) {
        listsSheet.getCell(2, col).value = '';
      } else {
        locations.forEach((locName, locIndex) => {
          listsSheet.getCell(locIndex + 2, col).value = locName;
        });
      }

      const endRow = Math.max(locations.length + 1, 2);
      const colLetter = listsSheet.getColumn(col).letter;
      workbook.definedNames.add(`Lists!$${colLetter}$2:$${colLetter}$${endRow}`, rangeKey);
    });

    // Cascading location dropdown based on selected emirate (spaces → underscores)
    for (let row = 2; row <= DATA_ROW_COUNT + 1; row += 1) {
      ordersSheet.getCell(row, LOCATION_COL).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`INDIRECT(SUBSTITUTE(G${row}," ","_"))`],
        showErrorMessage: true,
        errorTitle: 'Invalid Location',
        error: 'Please select a Location for the chosen Emirate'
      };
    }

    // Helper note sheet so users know how cascading works
    const helpSheet = workbook.addWorksheet('Instructions');
    helpSheet.getColumn(1).width = 90;
    helpSheet.addRow(['How to use this template']);
    helpSheet.getRow(1).font = { bold: true, size: 14 };
    helpSheet.addRow(['1. Select PayType from the dropdown (COD or CAD).']);
    helpSheet.addRow(['2. Select Emirate from the dropdown (values from your system).']);
    helpSheet.addRow(['3. Select Location from the dropdown — options depend on the Emirate you chose.']);
    helpSheet.addRow(['4. Fill in the remaining columns, then upload this file in Bulk Order Entry.']);
    helpSheet.addRow(['Note: Open in Microsoft Excel or compatible apps that support data validation dropdowns.']);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer, fileNames[type]);
};
